import {
  trackDubGenerateCompleted,
  trackDubGenerateFailed,
  trackDubGenerateStarted,
} from '@src/analytics/EventTracker';
import Checkbox from '@src/components/common/Checkbox';
import {
  DialogFooter,
  DialogHeader,
  DialogText,
  DialogTitle,
  PrimaryButton,
  SecondaryButton,
} from '@src/components/common/dialogStyles';
import Modal from '@src/components/common/Modal';
import ProgressBar from '@src/components/common/ProgressBar';
import { MODAL_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import { NESTED_IN_MODAL_Z } from '@src/components/common/topLayerZ';
import AssignSpeakerModal from '@src/components/sidepanel/modals/AssignSpeakerModal';
import SimpleLoadingSpinner from '@src/components/SimpleLoadingSpinner';
import SpeakerFormModal from '@src/components/transcript/SpeakerFormModal';
import { estimateDubGenerateMs } from '@src/config/processingEstimates';
import { useSpeakerManagementService } from '@src/hooks/useSpeakerManagementService';
import { DubbingApiError } from '@src/repositories/DubbingRepository';
import { resolveAssignedSpeakers } from '@src/services/SpeakerManagementService';
import { useDubbingStore, useTranscription } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { InfoIconSmall } from '@src/svgs/InfoIconSmall';
import { normalizeLangKey } from '@src/utility/bcp47';
import { extractErrorInfo, isAbortError } from '@src/utility/error';
import { formatLanguageCaption } from '@src/utility/languageCaption';
import { LogManager } from '@src/utility/Logger';
import {
  extractSpeakerLabelsFromTranscriptElements,
  getSpeakerColorFromLabel,
  getSpeakerNameFromLabel,
} from '@src/utility/speakerUtils';
import { observer } from 'mobx-react-lite';
import {
  type CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import DubDirectionField from './DubDirectionField';
import DubRunVoiceMapper from './DubRunVoiceMapper';
import ManageAiVoicesModal from './ManageAiVoicesModal';
import { useDubDirectionState } from './useDubDirectionState';
import { useDubGenerateProgress } from './useDubGenerateProgress';
import { useVoiceActivation } from './useVoiceActivation';

import type { UnassignedTranscriptVoice } from './DubRunVoiceMapper';
import type { DubMusicOption } from './useKeepOriginalMusic';
import type { DubGenerateResult } from '@src/repositories/DubbingRepository';
import type {
  SpeakerData,
  SpeakerWithColor,
} from '@src/services/SpeakerManagementService';
import type { Language } from '@src/types.ts/language';

const logger = LogManager.getLogger('aiVoices:DubGenerateModal');

// "Alice", "Alice and Bob", "Alice, Bob and Carol".
const listNames = (names: string[]): string =>
  names.length <= 1
    ? (names[0] ?? '')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

const pluralLines = (n: number) => (n === 1 ? '1 line' : `${n} lines`);

export interface DubGenerateModalProps {
  storyId: string;
  /** The translated language to dub into (BCP-47). */
  targetLanguage: string;
  /** Workspace supported languages (for the language caption + DI19 pin key). */
  supportedLanguages: Language[];
  /**
   * Replace mode (PLAT-10592): a dub already exists for this language. Switches
   * the title/reminder/button to "Replace" and sends `regenerate: true` — a
   * wholesale redo over the engine's soft-delete (the prior dub stays in
   * history). Defaults to a first-time generate.
   */
  replace?: boolean;
  /**
   * Span re-dub mode (PLAT-10616): re-synthesize only these period-level
   * segments (the covered `parentId`s of a translated-text selection), keyed
   * as the engine's `segmentIds`. When present, the popover opens scoped to the
   * selected section — titled "Re-dub AI Voice", the per-speaker voices are
   * shown but LOCKED (this is a targeted re-run in the speakers' current
   * voices, not a re-selection), `regenerate: true` forces re-synthesis of just
   * these segments, and the rest of the dub is reused (additive, DE2). Omit for
   * a whole-language generate/replace.
   */
  segmentIds?: string[];
  /**
   * Fill-the-gaps mode: the `segmentIds` are lines that have NO audio, so the run
   * is additive (`regenerate: false`). Anything already voiced is reused rather
   * than redone, because redoing a line costs a voice-provider call and replaces
   * audio the creator may already have approved. Requires `segmentIds`.
   */
  fillGaps?: boolean;
  /**
   * First-dub mode (PLAT-11440): the `segmentIds` are one clip's lines, and the
   * story has no dub in this language at all.
   *
   * The other two scoped modes repair a dub that exists — they reuse each
   * speaker's current voice and say what is being kept. Here there is no dub, so
   * there is no current voice to reuse and nothing to keep: the run is a first
   * generate that happens to name one clip's lines. So the popover presents
   * exactly as the whole-story first generate does — "Dub with AI Voice", a
   * voice the creator chooses per speaker, and a "Generate" button — while still
   * sending only the named lines.
   *
   * Meaningless without `segmentIds`, and ignored there, so setting it can never
   * turn a whole-story run into something else.
   */
  firstDub?: boolean;
  /**
   * Source video length in seconds, used only to tune the progress-bar ETA
   * (see {@link useDubGenerateProgress}). Falls back to a fixed estimate when
   * omitted.
   */
  videoDurationSec?: number;
  /**
   * How much room the dub has on the STORY's timeline, in milliseconds.
   * Forwarded to the engine untouched.
   *
   * Without it the engine's ceiling is the end of the last spoken word, which a
   * translation always overruns, so every later run re-records the whole story
   * to reclaim milliseconds it can never win back. The source video's true
   * length removes that false overflow. Never a clip's own length.
   */
  availableDurationMs?: number;
  /**
   * The "Keep Original Music" checkbox. Built by the host with
   * `useKeepOriginalMusic`, which outlives this popover — a separation the box
   * starts runs alongside the dub and may land after the popover is gone.
   * Omitted ⇒ no checkbox.
   */
  music?: DubMusicOption;
  onClose: () => void;
  /** Fires with the run's coverage after a successful generate. */
  onSuccess?: (result: DubGenerateResult) => void;
  /**
   * PLAT-11662 — an action for the "dub ready" toast (mirrors the translation
   * modal's): hosts that carry the Review workspace pass a "Review Dub" entry so
   * hearing the result is one click from the toast. Omitted ⇒ plain toast.
   * Fires only on a clean, complete run.
   */
  successAction?: (
    targetLanguage: string,
  ) => { label: string; onClick: () => void } | undefined;
}

const DUB_GENERATE_TOOLTIP_ID = 'dub-generate-tooltip';

const CONTENT_STYLE: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  width: 'min(720px, calc(100vw - 32px))',
  maxHeight: 'calc(100vh - 128px)',
  overflow: 'hidden',
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-card)',
  padding: 'var(--spacing-lg)',
};

// The kick-off "Generate dub" popover (DE5/DE8), launched from the Transcript &
// Speakers language drop-down. Hosts the per-speaker→AI-Voice run mapper and
// fires the dub-generate engine (PLAT-10590) with the resolved voice map, keyed
// by the internal spk_X id the manifest segments use (DS3). Presents like the
// translation generate modal for surface parity (DE8).
const DubGenerateModal = observer(
  ({
    storyId,
    targetLanguage,
    supportedLanguages,
    replace = false,
    segmentIds,
    fillGaps = false,
    firstDub = false,
    videoDurationSec,
    availableDurationMs,
    music,
    onClose,
    onSuccess,
    successAction,
  }: DubGenerateModalProps) => {
    // Three scoped modes, same scoping, different arithmetic on the segments they
    // name: a span re-dub FORCES re-synthesis of lines that already have audio;
    // filling gaps is ADDITIVE over lines that have none; a first dub is
    // additive too, over a language with no dub at all.
    //
    // The first two repair an existing dub, so they reuse each speaker's current
    // voice and lock the picker. The third has no dub behind it, so there is no
    // current voice to reuse: it presents as the first generate it is, and the
    // creator chooses.
    const isScoped = !!segmentIds?.length;
    const isFirstDub = isScoped && firstDub;
    const isFillGaps = isScoped && fillGaps && !isFirstDub;
    const isSpanReDub = isScoped && !fillGaps && !isFirstDub;
    const dubbingStore = useDubbingStore();
    const videoCreator = useVideoCreatorStore();
    const speakerService = useSpeakerManagementService();
    const transcriptionStore = useTranscription();
    const { organization } = useVoiceActivation();

    const [speakers, setSpeakers] = useState<SpeakerWithColor[]>([]);
    // Transcript label → DatoCMS speaker id: spk_X voice mappings plus the
    // override_<datoId> labels of section assignments (the manifest keys its
    // segments on the transcript label, DS3).
    const [labelToDatoId, setLabelToDatoId] = useState<Record<string, string>>(
      {},
    );
    // The story's and the showcase's full speaker lists, and every `spk_X` the
    // transcript carries — the raw material for the in-modal assign step,
    //, which is what makes an empty mapper recoverable.
    const [storySpeakers, setStorySpeakers] = useState<SpeakerWithColor[]>([]);
    const [orgSpeakers, setOrgSpeakers] = useState<SpeakerWithColor[]>([]);
    const [transcriptLabels, setTranscriptLabels] = useState<string[]>([]);
    // The story's spk_X -> speaker id mappings as fetched. `createAndAssignSpeaker`
    // writes `{...these, [label]: newId}` as a FULL replacement of the story's
    // mappings, so a mapping missing here is a mapping deleted from the story.
    const [speakerMappings, setSpeakerMappings] = useState<
      Record<string, string>
    >({});
    // The transcript voice being assigned, and whether the creator chose to
    // make a new speaker for it rather than pick an existing one.
    const [assignVoice, setAssignVoice] =
      useState<UnassignedTranscriptVoice | null>(null);
    const [creatingForVoice, setCreatingForVoice] =
      useState<UnassignedTranscriptVoice | null>(null);
    // Effective resolved voice per speaker (built by each row's report effect);
    // the source for the voiceBySpeaker request map. An empty string is a row
    // that has reported and has NO voice for the run language — kept apart from
    // "not reported yet" so the footer can name the speaker without flashing
    // every name for the one render before the rows report (PLAT-11569).
    const [selections, setSelections] = useState<Record<string, string>>({});
    // Deliberate user picks only (PLAT-10727) — kept apart from `selections` so
    // an auto-resolved default can't act as a tier-1 override. Feeds the
    // resolver's runOverride; a speaker the user hasn't picked re-resolves to
    // the current language default after a Manage change.
    const [userOverrides, setUserOverrides] = useState<Record<string, string>>(
      {},
    );
    const [manageSpeaker, setManageSpeaker] = useState<SpeakerWithColor | null>(
      null,
    );
    // Bumped per speaker when its Manage modal closes so that row's separate
    // useSpeakerAiVoices instance re-syncs from the (already-updated) cache —
    // otherwise voices added in Manage don't appear until the overlay reopens.
    const [reloadTokens, setReloadTokens] = useState<Record<string, number>>(
      {},
    );
    const [loading, setLoading] = useState(true);
    // `null` until the creator touches the box, so it keeps following the
    // host's default while the separation state is still being read.
    const [keepMusicChoice, setKeepMusicChoice] = useState<boolean | null>(
      null,
    );
    const musicUnavailable = !music || !!music.unavailableReason;
    const keepMusic =
      !musicUnavailable && (keepMusicChoice ?? music.defaultChecked);
    const [error, setError] = useState<string | null>(null);
    // The run in flight lives in the store (PLAT-11570), so closing this
    // popover neither stops it nor loses it: reopening finds it still running
    // and any language toggle shows it. This popover is "generating" when the
    // store's run is this story + language.
    const activeRun = dubbingStore.activeRun;
    const running =
      activeRun !== null &&
      activeRun.storyId === storyId &&
      normalizeLangKey(activeRun.targetLanguage) ===
        normalizeLangKey(targetLanguage);
    // Belt-and-suspenders: the catalog is hydrated at workspace init, but
    // hydrate() is idempotent so this is a no-op if already loaded.
    useEffect(() => {
      if (organization?.id) void dubbingStore.hydrate(organization.id);
    }, [organization?.id, dubbingStore]);

    // Fetch the story's assigned speakers + label→dato map. Assigned is the
    // Speakers panel's definition (PLAT-11583): a speaker a transcript voice
    // (spk_X) is mapped to OR one assigned to a passage by section override.
    // Both kinds have lines the dub must voice.
    useEffect(() => {
      let cancelled = false;
      setLoading(true);
      void speakerService.fetchSpeakersForStory(storyId).then(result => {
        if (cancelled) return;
        if (!result.ok) {
          setError(result.error.message);
          setLoading(false);
          return;
        }
        const {
          storySpeakers: fetchedStorySpeakers,
          orgSpeakers: fetchedOrgSpeakers,
          mappings,
        } = result.value;
        const elements =
          transcriptionStore?.getOriginalTranscription()?.elements ?? [];
        const { assigned, labelToSpeakerId } = resolveAssignedSpeakers({
          storyId,
          storySpeakers: fetchedStorySpeakers,
          mappings,
          transcriptElements: elements,
        });
        setSpeakers(assigned);
        setLabelToDatoId(labelToSpeakerId);
        setStorySpeakers(fetchedStorySpeakers);
        setOrgSpeakers(fetchedOrgSpeakers);
        setTranscriptLabels(
          extractSpeakerLabelsFromTranscriptElements(elements),
        );
        setSpeakerMappings(mappings);
        setLoading(false);
      });
      return () => {
        cancelled = true;
      };
    }, [storyId, speakerService, transcriptionStore]);

    const runLanguage = useMemo(
      () => supportedLanguages.find(l => l.value === targetLanguage) ?? null,
      [supportedLanguages, targetLanguage],
    );
    const languageCaption =
      runLanguage?.caption ?? formatLanguageCaption(targetLanguage);

    // The transcript's own voices that no speaker stands behind. Only real
    // `spk_X` labels: an `override_<datoId>` label is by definition already a
    // speaker, so it is never something to assign.
    const unassignedVoices = useMemo<UnassignedTranscriptVoice[]>(
      () =>
        transcriptLabels
          .filter(label => label.startsWith('spk_') && !labelToDatoId[label])
          .map(label => ({
            label,
            name: getSpeakerNameFromLabel(label),
            color: getSpeakerColorFromLabel(label),
          })),
      [transcriptLabels, labelToDatoId],
    );

    // A speaker has just been put behind `label`. Fold it into local state
    // rather than re-fetching: the DatoCMS CDA lags a fresh write, so a re-fetch
    // here would show the mapper empty again for the seconds the creator is
    // most likely to be looking at it (the same reason the Speakers panel
    // updates optimistically).
    const applyAssignment = useCallback(
      (label: string, speaker: SpeakerWithColor) => {
        setLabelToDatoId(prev => ({ ...prev, [label]: speaker.id }));
        setSpeakers(prev =>
          prev.some(s => s.id === speaker.id) ? prev : [...prev, speaker],
        );
        setStorySpeakers(prev =>
          prev.some(s => s.id === speaker.id) ? prev : [...prev, speaker],
        );
        setSpeakerMappings(prev => ({ ...prev, [label]: speaker.id }));
      },
      [],
    );

    const handleAssignExisting = useCallback(
      async (datoSpeakerId: string): Promise<boolean> => {
        if (!assignVoice) return false;
        const result = await speakerService.assignSpeaker(
          storyId,
          assignVoice.label,
          datoSpeakerId,
        );
        if (!result.ok) {
          setError(result.error.message);
          return false;
        }
        const speaker = [...storySpeakers, ...orgSpeakers].find(
          s => s.id === datoSpeakerId,
        );
        if (speaker) applyAssignment(assignVoice.label, speaker);
        setAssignVoice(null);
        return true;
      },
      [
        assignVoice,
        speakerService,
        storyId,
        storySpeakers,
        orgSpeakers,
        applyAssignment,
      ],
    );

    const handleCreateAndAssign = useCallback(
      async (data: SpeakerData): Promise<boolean> => {
        if (!creatingForVoice) return false;
        const result = await speakerService.createAndAssignSpeaker(
          storyId,
          videoCreator.story?.primaryShowcase?.id,
          creatingForVoice.label,
          data,
          speakerMappings,
        );
        if (!result.ok) {
          setError(result.error.message);
          return false;
        }
        applyAssignment(creatingForVoice.label, {
          ...result.value,
          color: creatingForVoice.color,
          // The CMA create response's headshots are `{ upload_id }` with no
          // displayable url; the just-uploaded ones carry it.
          headshots: data.headshots ?? undefined,
        });
        setCreatingForVoice(null);
        return true;
      },
      [
        creatingForVoice,
        speakerService,
        storyId,
        videoCreator,
        speakerMappings,
        applyAssignment,
      ],
    );

    const handleSelect = useCallback(
      (speakerId: string, voiceId: string | undefined) => {
        setSelections(prev => {
          const next = voiceId ?? '';
          if (prev[speakerId] === next) return prev;
          return { ...prev, [speakerId]: next };
        });
      },
      [],
    );

    // Record a deliberate pick in the picker as a tier-1 run override
    // (PLAT-10727). The row's report effect then propagates the resolved voice
    // into `selections` for the request map.
    const handleUserPick = useCallback((speakerId: string, voiceId: string) => {
      setUserOverrides(prev =>
        prev[speakerId] === voiceId ? prev : { ...prev, [speakerId]: voiceId },
      );
    }, []);

    // Build the resolved label → aiVoiceId request map (DS3): a speaker with no
    // resolved voice is simply omitted (the engine skips it).
    const voiceBySpeaker = useMemo(() => {
      const map: Record<string, string> = {};
      for (const [label, datoId] of Object.entries(labelToDatoId)) {
        const voiceId = selections[datoId];
        if (voiceId) map[label] = voiceId;
      }
      return map;
    }, [labelToDatoId, selections]);

    const dubbableCount = Object.keys(voiceBySpeaker).length;
    // Every shown speaker must have a (language-valid) voice before generating —
    // the run dubs all of them, so a partial map would silently skip speakers.
    const requiredCount = useMemo(() => {
      const shown = new Set(speakers.map(s => s.id));
      return Object.values(labelToDatoId).filter(id => id && shown.has(id))
        .length;
    }, [labelToDatoId, speakers]);
    const canGenerate =
      !running &&
      !loading &&
      requiredCount > 0 &&
      dubbableCount === requiredCount;

    // Why Generate is disabled, in one line above the footer (PLAT-11569): the
    // speakers whose row has reported no voice for the run language. The
    // no-speakers case is already stated by the mapper's empty state.
    const speakersMissingVoice = useMemo(
      () => speakers.filter(s => selections[s.id] === '').map(s => s.name),
      [speakers, selections],
    );
    const disabledReason =
      !loading && !running && speakersMissingVoice.length > 0
        ? `${listNames(speakersMissingVoice)} still ${
            speakersMissingVoice.length === 1 ? 'needs' : 'need'
          } an AI Voice for ${languageCaption} before you can ${
            replace ? 'replace' : 'generate'
          } the dub.`
        : null;

    // PLAT-12022 — the direction block (note, voice settings, delivery markers,
    // the capability read and the pre-fill) lives in one hook shared with the
    // selection re-dub popover, so both ask the same questions and send the
    // same thing.
    const directionState = useDubDirectionState({
      storyId,
      language: targetLanguage,
      ...(isScoped && segmentIds ? { segmentIds } : {}),
      scopeToSpanSpeakers: isSpanReDub,
      labelToDatoId,
      selections,
      speakers,
    });
    const { direction, blocked: directionBlocked } = directionState;

    // Smooth estimated progress while the (percentage-less) synthesis runs,
    // reusing the publish drawer's time-interpolation idiom (PLAT-10707).
    // Anchored to the store's run so it continues where it was if the popover
    // is reopened mid-run.
    const progress = useDubGenerateProgress(
      running,
      running ? activeRun : { videoDurationSec, speakerCount: dubbableCount },
    );

    const handleGenerate = useCallback(async () => {
      if (dubbableCount === 0) return;
      // PLAT-11797 — a control set outside what the models declared stops the
      // run here rather than starting an undirected one behind the person's
      // back. The button is disabled for the same reason; this is the second
      // guard, for a keyboard submit that gets past it.
      if (directionBlocked) return;
      const runSegmentIds = isScoped ? segmentIds : undefined;
      setError(null);
      const regenerate = isFirstDub
        ? false
        : isFillGaps
          ? false
          : isSpanReDub
            ? true
            : replace;
      trackDubGenerateStarted({
        story_id: storyId,
        language: targetLanguage,
        speaker_count: dubbableCount,
        // Filling gaps must not force re-synthesis, or it would redo audio the
        // creator already approved.
        regenerate,
        scope: isFirstDub
          ? 'clip-first'
          : isFillGaps
            ? 'clip-gaps'
            : isSpanReDub
              ? 'span'
              : 'language',
        segment_count: runSegmentIds?.length,
      });
      try {
        // PLAT-11688 — kick the run off, then get out of the way at once. The
        // page already carries a running dub (the chip and its progress
        // readout, PLAT-11570 / PLAT-11626), so holding the screen to replay
        // the same progress makes the creator dismiss a popover to reach the
        // work they were doing. `startRun` registers the run synchronously
        // before its first await, so the chip is live the instant this closes.
        // The await below still lands the outcome: closing mid-run was always
        // allowed, so every branch already has its "popover is gone" path.
        const pending = dubbingStore.startRun(
          {
            storyId,
            targetLanguage,
            voiceBySpeaker,
            segmentIds: runSegmentIds,
            regenerate,
            availableDurationMs,
            ...(direction ? { direction } : {}),
          },
          {
            languageCaption,
            etaMs: estimateDubGenerateMs({
              videoDurationSec,
              speakerCount: dubbableCount,
            }),
          },
        );
        // Separation reads only the source recording, so it runs alongside
        // the dub rather than after it.
        music?.apply(keepMusic);
        onClose();
        const result = await pending;
        trackDubGenerateCompleted({
          story_id: storyId,
          language: targetLanguage,
          run_id: result.runId,
          synthesized_count: result.coverage.synthesized.length,
          reused_count: result.coverage.reused.length,
          skipped_count: result.coverage.skipped.length,
          failed_count: result.coverage.failed.length,
        });
        // The run succeeded for everything it does not list as failed, and the
        // host outlives this popover, so its onSuccess still lands the result
        // (mic, audio mode) even when the popover was closed mid-run.
        onSuccess?.(result);
        if (result.coverage.failed.length > 0) {
          // PLAT-11688 — the popover is gone by now, so the toast is where a
          // partial dub is reported. It replaces PLAT-11569's in-popover list
          // and its scoped Retry, which had nowhere left to live.
          videoCreator.showToast({
            state: 'error',
            message: `${languageCaption} dub finished, but ${pluralLines(
              result.coverage.failed.length,
            )} couldn’t be dubbed.`,
          });
          return;
        }
        // Confirm only — no next step (PLAT-11569). A repair of an existing dub
        // says "updated" rather than announcing a dub that was already there.
        videoCreator.showToast({
          state: 'success',
          message: `${languageCaption} dub ${
            isSpanReDub || isFillGaps ? 'updated' : 'ready'
          }`,
          action: successAction?.(targetLanguage),
        });
      } catch (err) {
        // Cancelled from this popover or the on-page chip: nothing to report.
        if (isAbortError(err)) return;
        const info = extractErrorInfo(err);
        logger.error('Dub generate failed', info);
        // A run-fatal voice-provider problem (out of credits / bad key,
        // PLAT-10741) isn't something the storyteller can fix — show a fixed
        // "contact support" message rather than the raw provider error.
        const isVoiceProviderError =
          err instanceof DubbingApiError && err.code === 'VOICE_PROVIDER_ERROR';
        const message = isVoiceProviderError
          ? 'There’s a problem with the voice provider. Please contact support.'
          : info.errorMessage || 'Dub generation failed. Try again.';
        // PLAT-11688 — likewise the failure message, contact-support wording
        // (PLAT-10741) included: the popover that used to show it has closed.
        videoCreator.showToast({
          state: 'error',
          message: `${languageCaption} dub failed. ${message}`,
        });
        trackDubGenerateFailed({
          story_id: storyId,
          language: targetLanguage,
          error_message: info.errorMessage || 'unknown',
        });
      }
    }, [
      dubbableCount,
      directionBlocked,
      isFirstDub,
      isFillGaps,
      isSpanReDub,
      replace,
      storyId,
      targetLanguage,
      isScoped,
      segmentIds,
      dubbingStore,
      voiceBySpeaker,
      // The story's duration can arrive after this popover mounts; without it in
      // the dependency list the ceiling captured at mount is the one sent.
      availableDurationMs,
      direction,
      languageCaption,
      videoDurationSec,
      onSuccess,
      successAction,
      targetLanguage,
      onClose,
      videoCreator,
      music,
      keepMusic,
    ]);

    // Cancel while running aborts the request (the store owns the handle) and
    // closes; "Continue in Background" just closes — as do Escape, the ✕ and
    // the backdrop, so a dismissed popover never orphans its run.
    const handleCancel = useCallback(() => {
      if (running) dubbingStore.cancelRun();
      onClose();
    }, [running, dubbingStore, onClose]);

    return (
      <Modal
        isOpen
        closeModal={onClose}
        contentStyle={CONTENT_STYLE}
        // While an assign/create dialog sits on top, Escape belongs to it.
        // Both of them listen on `document` as this one does, so without the
        // guard one keypress closes the nested dialog AND the run being set up.
        disableEscapeClose={!!assignVoice || !!creatingForVoice}
      >
        <Header>
          <DialogTitle>
            {isFillGaps
              ? 'Dub the missing lines'
              : isSpanReDub
                ? 'Re-dub AI Voice'
                : replace
                  ? `Replace ${languageCaption} dub`
                  : 'Dub with AI Voice'}
          </DialogTitle>
          <Subtitle>
            {isFillGaps
              ? `Dub the lines of this clip that have no ${languageCaption} voice yet, in each speaker’s current AI Voice.`
              : isSpanReDub
                ? `Re-synthesize the ${languageCaption} dub for the selected section, in each speaker’s current AI Voice.`
                : `Choose each speaker’s AI Voice, then ${
                    replace ? 'replace' : 'generate'
                  } the ${languageCaption} dub.`}
          </Subtitle>
        </Header>
        {isFirstDub ? (
          <ReplaceNotice role="status" data-testid="dub-scope-notice">
            You’re dubbing this clip only —{' '}
            {segmentIds.length === 1
              ? 'the one line it covers'
              : `the ${segmentIds.length} lines it covers`}
            . The rest of the video keeps its original voice.
          </ReplaceNotice>
        ) : isFillGaps ? (
          <ReplaceNotice role="status" data-testid="dub-scope-notice">
            You’re dubbing{' '}
            {segmentIds.length === 1
              ? 'the one line'
              : `the ${segmentIds.length} lines`}{' '}
            this clip is missing. Everything already dubbed is kept as it is —
            nothing is re-recorded.
          </ReplaceNotice>
        ) : isSpanReDub ? (
          <ReplaceNotice role="status" data-testid="dub-scope-notice">
            You’re re-dubbing the selected section
            {segmentIds.length > 1 ? ` (${segmentIds.length} segments)` : ''}.
            The rest of the {languageCaption} dub is kept.
          </ReplaceNotice>
        ) : (
          replace && (
            <ReplaceNotice role="status" data-testid="dub-scope-notice">
              You’re replacing the existing {languageCaption} dub. The current
              dub is kept in your history.
            </ReplaceNotice>
          )
        )}
        <Body>
          {loading ? (
            <LoadingRow>
              <SimpleLoadingSpinner />
            </LoadingRow>
          ) : (
            <DubRunVoiceMapper
              speakers={speakers}
              language={runLanguage}
              userOverrides={userOverrides}
              reloadTokens={reloadTokens}
              onSelect={handleSelect}
              onUserPick={handleUserPick}
              onManageSpeaker={setManageSpeaker}
              unassignedVoices={unassignedVoices}
              onAssignVoice={setAssignVoice}
              busy={running}
              // The two REPAIR modes reuse the speakers' current voices — shown,
              // but not changeable. A first dub has no current voice to reuse,
              // so it is chosen here like any other first generate.
              readOnly={isScoped && !isFirstDub}
              // PLAT-12023 — each speaker's play button previews their words
              // with the direction the run would send.
              previewFor={directionState.previewFor}
            />
          )}
        </Body>
        {music && !running && (
          <MusicOption>
            <div className="row">
              <Checkbox
                label="Keep Original Music"
                checked={keepMusic}
                color={keepMusic ? 'var(--accent)' : 'transparent'}
                borderColor={keepMusic ? 'var(--accent)' : 'var(--border)'}
                labelColor="var(--foreground)"
                disabled={musicUnavailable}
                onClick={() => {
                  if (!musicUnavailable) setKeepMusicChoice(!keepMusic);
                }}
              />
              {/* PLAT-12041 — what the option does lives behind an info
                  icon: one checkbox does not need a paragraph under it. */}
              <span
                className="info"
                // Reachable by keyboard, like every info glyph: focusing it
                // shows the same tooltip as hovering.
                tabIndex={0}
                data-testid="keep-music-info"
                data-tooltip-id={DUB_GENERATE_TOOLTIP_ID}
                data-tooltip-content={
                  !music.needsSeparation
                    ? 'Play the original recording’s music and sounds under the dub but remove the original voice.'
                    : music.separating
                      ? 'Separating the music from the voice now — it plays under the dub once ready.'
                      : 'Separates the music from the voice while the dub generates. This can take a few minutes.'
                }
                aria-label="About Keep Original Music"
              >
                <InfoIconSmall />
              </span>
            </div>
            {/* A box that cannot be ticked says why in sight: a reason behind
                a hover would be found only by someone already puzzled. */}
            {music.unavailableReason && (
              <p className="hint">{music.unavailableReason}</p>
            )}
          </MusicOption>
        )}
        {/* PLAT-11384 / PLAT-11385 — how this run can be directed, and the
            note field when it can be. In every mode, because every mode makes
            audio. What is shown follows the voices' models and nothing else:
            a run whose voices cannot take a written direction shows the reason
            and no field, rather than a field the engine would refuse. */}
        <DubDirectionField {...directionState.fieldProps} disabled={running} />
        {error && <ErrorText role="alert">{error}</ErrorText>}
        <Footer>
          {running ? (
            <GeneratingRow>
              <div className="line">
                <span className="label">
                  {isFillGaps
                    ? 'Dubbing the missing lines of the'
                    : isSpanReDub
                      ? 'Re-dubbing'
                      : replace
                        ? 'Replacing'
                        : 'Generating'}{' '}
                  {languageCaption} dub…
                </span>
                <span className="pct">{Math.round(progress)}%</span>
              </div>
              <ProgressBar value={progress} max={100} height={4} />
            </GeneratingRow>
          ) : (
            <span />
          )}
          <FooterButtons>
            <SecondaryButton type="button" onClick={handleCancel}>
              Cancel
            </SecondaryButton>
            {running ? (
              <PrimaryButton type="button" onClick={onClose}>
                Continue in Background
              </PrimaryButton>
            ) : (
              // The one-line reason a disabled Generate has (PLAT-11569) is
              // its hover tooltip. A disabled button takes no pointer events,
              // so the span around it is what the tooltip listens to.
              <TooltipAnchor
                data-tooltip-id={DUB_GENERATE_TOOLTIP_ID}
                data-tooltip-content={disabledReason ?? undefined}
              >
                <PrimaryButton
                  type="button"
                  onClick={() => void handleGenerate()}
                  // PLAT-11797 — a voice setting outside what the models
                  // declared holds the run: the panel says which one, and the
                  // backend would refuse it anyway, after the person had waited.
                  disabled={!canGenerate || directionBlocked}
                >
                  {isFillGaps
                    ? 'Dub the missing lines'
                    : isSpanReDub
                      ? 'Re-dub'
                      : replace
                        ? 'Replace'
                        : 'Generate'}
                </PrimaryButton>
              </TooltipAnchor>
            )}
          </FooterButtons>
        </Footer>
        <Tooltip id={DUB_GENERATE_TOOLTIP_ID} style={MODAL_TOOLTIP_STYLE} />

        {manageSpeaker && (
          <ManageAiVoicesModal
            isOpen
            onClose={() => {
              const managedId = manageSpeaker.id;
              setReloadTokens(prev => ({
                ...prev,
                [managedId]: (prev[managedId] ?? 0) + 1,
              }));
              setManageSpeaker(null);
            }}
            speakerId={manageSpeaker.id}
            speakerName={manageSpeaker.name}
            speakerHeadshotUrl={manageSpeaker.headshots?.[0]?.url ?? null}
            speakerColor={manageSpeaker.color}
            // The run has one language in mind, so land on it (PLAT-11581).
            initialLanguageId={runLanguage?.id}
          />
        )}

        {/* The way out of an empty mapper: put a speaker behind a
            transcript voice without leaving the run being set up. The same two
            modals and the same service calls the Speakers panel uses, so an
            assignment made here is the one the panel shows. */}
        {assignVoice && (
          <AssignSpeakerModal
            isOpen
            // Both overlays portal to `document.body` as siblings of this
            // dialog's own backdrop, which rests higher than theirs — without
            // the lift they open behind the popover that asked for them.
            zIndex={NESTED_IN_MODAL_Z}
            onClose={() => setAssignVoice(null)}
            onAssign={handleAssignExisting}
            storySpeakers={storySpeakers}
            organizationSpeakers={orgSpeakers}
            speakerLabel={assignVoice.label}
            speakerDisplayName={assignVoice.name}
            onCreateNewSpeaker={() => {
              setCreatingForVoice(assignVoice);
              setAssignVoice(null);
            }}
          />
        )}
        {creatingForVoice && (
          <SpeakerFormModal
            isOpen
            zIndex={NESTED_IN_MODAL_Z}
            onClose={() => setCreatingForVoice(null)}
            onSubmit={handleCreateAndAssign}
          />
        )}
      </Modal>
    );
  },
);

export default DubGenerateModal;

const Header = styled(DialogHeader)`
  margin-bottom: var(--spacing-md);
  flex-shrink: 0;
`;

// DialogText defaults to --text-secondary; this modal's subcopy uses the softer
// --text-muted.
const Subtitle = styled(DialogText)`
  color: var(--text-muted);
`;

// The mode reminder (PLAT-10592): what this run replaces or keeps. A quiet
// note, not a callout — the design system's tinted, bordered box is for risk
// notes, and this line says nothing is lost. A thin gold rule marks it as a
// note without shouting.
const ReplaceNotice = styled.div`
  margin-bottom: var(--spacing-md);
  padding: 2px 0 2px 10px;
  border-left: 2px solid var(--accent);
  font-size: var(--text-sm);
  line-height: 1.4;
  color: var(--text-secondary);
  flex-shrink: 0;
`;

// The speaker list's frame lives here, on the scroll container, so the
// rounded corners stay in view however long the list is.
const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  border: 1px solid var(--border);
  border-radius: var(--radius);
`;

// The "Keep Original Music" option: the box, with a muted line under it saying
// what ticking it does (or why it can't be ticked).
const MusicOption = styled.div`
  margin-top: var(--spacing-md);
  flex-shrink: 0;

  .row {
    display: flex;
    align-items: center;
    gap: var(--spacing-xs);
  }
  .info {
    display: inline-flex;
    align-items: center;
    border-radius: 50%;
    color: var(--text-secondary);
    cursor: help;

    &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 1px var(--focus-ring);
    }
  }
  .hint {
    margin: 4px 0 0 22px;
    font-size: var(--text-xs);
    color: var(--text-muted);
  }
`;

const LoadingRow = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px 0;
`;

const ErrorText = styled.p`
  margin: 12px 0 0;
  font-size: var(--text-sm);
  color: var(--destructive);
`;

const TooltipAnchor = styled.span`
  display: inline-flex;
`;

const Footer = styled(DialogFooter)`
  align-items: center;
  justify-content: space-between;
  margin-top: var(--spacing-lg);
  flex-shrink: 0;
`;

const FooterButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// Left side of the footer while generating: the status label + a live percent
// over a thin progress bar (interpolated estimate — the engine gives no real
// percentage). Flexes to fill the space beside the action buttons.
const GeneratingRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 0;
  max-width: 320px;
  margin-right: var(--spacing-md);
  color: var(--text-secondary);
  font-size: var(--text-sm);

  .line {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
  }
  .label {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .pct {
    flex-shrink: 0;
    color: var(--text-muted);
    font-variant-numeric: tabular-nums;
  }
`;
